import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Workspace } from "@/hooks/useWorkspace";

export type ReviewItem = {
  id: string;
  title: string;
  display_title: string | null;
  subtitle: string | null;
  author_text: string | null;
  workspace_id: string;
  workspaceIds: string[];
};

export type ReviewExcerpt = {
  id: string;
  body_text: string;
  workspace_id: string;
  library_item_id: string | null;
  libraryItemTitle: string | null;
};

type Args = {
  doomedWorkspaceIds: string[];
  doomedSet: Set<string>;
  workspaces: Workspace[];
};

export const useDowngradeReviewData = ({ doomedWorkspaceIds, doomedSet }: Args) => {
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [excerpts, setExcerpts] = useState<ReviewExcerpt[]>([]);
  const [loading, setLoading] = useState(true);

  const doomedKey = doomedWorkspaceIds.join(",");

  const load = useCallback(async () => {
    if (doomedWorkspaceIds.length === 0) {
      setItems([]);
      setExcerpts([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const { data: rows, error } = await supabase
      .from("library_items")
      .select("id, title, display_title, subtitle, author_text, workspace_id")
      .in("workspace_id", doomedWorkspaceIds)
      .order("title", { ascending: true });

    if (error) {
      toast.error(error.message);
      setItems([]);
      setExcerpts([]);
      setLoading(false);
      return;
    }

    const ids = (rows ?? []).map((r) => r.id);
    const visMap = new Map<string, string[]>();
    if (ids.length > 0) {
      const { data: linkRows } = await supabase
        .from("library_item_workspaces")
        .select("library_item_id, workspace_id")
        .in("library_item_id", ids);
      for (const link of linkRows ?? []) {
        const arr = visMap.get(link.library_item_id) ?? [];
        arr.push(link.workspace_id);
        visMap.set(link.library_item_id, arr);
      }
    }

    const atRiskItems: ReviewItem[] = (rows ?? [])
      .map((r) => {
        const wsIds = visMap.get(r.id) ?? [r.workspace_id];
        return { ...r, workspaceIds: wsIds } as ReviewItem;
      })
      .filter((r) => r.workspaceIds.every((wid) => doomedSet.has(wid)));

    setItems(atRiskItems);

    // Excerpts in doomed workspaces. Excerpts are workspace-bound by FK (no
    // cross-workspace visibility), so any excerpt in a doomed workspace is
    // at risk regardless of its parent library item's status.
    const { data: excerptRows, error: exErr } = await supabase
      .from("excerpts")
      .select("id, body_text, workspace_id, library_item_id")
      .in("workspace_id", doomedWorkspaceIds)
      .order("created_at", { ascending: false })
      .limit(500);
    if (exErr) {
      toast.error(exErr.message);
      setExcerpts([]);
    } else {
      const itemIds = Array.from(
        new Set((excerptRows ?? []).map((e) => e.library_item_id).filter(Boolean) as string[]),
      );
      const titleMap = new Map<string, string>();
      if (itemIds.length > 0) {
        const { data: titleRows } = await supabase
          .from("library_items")
          .select("id, title, display_title")
          .in("id", itemIds);
        for (const r of titleRows ?? []) {
          titleMap.set(r.id, r.display_title || r.title);
        }
      }
      setExcerpts(
        (excerptRows ?? []).map((e) => ({
          id: e.id,
          body_text: e.body_text,
          workspace_id: e.workspace_id,
          library_item_id: e.library_item_id,
          libraryItemTitle: e.library_item_id ? titleMap.get(e.library_item_id) ?? null : null,
        })),
      );
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doomedKey]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(
    () => ({ items, setItems, excerpts, setExcerpts, loading, reload: load }),
    [items, excerpts, loading, load],
  );
};
